import { initContract } from '@ts-rest/core'
import { z } from 'zod'
import { HistoryTables } from '../../shared'
import { ErrorResponseSchema } from '../../shared/types'
import {
  CancelSubscriptionExtraBodySchema,
  CancelSubscriptionSchema,
  CreateSubscriptionBodySchema,
  CreateSubscriptionDiscountBodySchema,
  CreateSubscriptionDiscountResponseSchema,
  DeleteMigratedSubscriptionDiscountQueryParamsSchema,
  DeleteSubscriptionDiscountBodySchema,
  DeleteSubscriptionPathParams,
  ExtraSchema,
  FeedingPlanSchema,
  GetSubscriptionQuerySchema,
  GetSubscriptionsByUserQuerySchema,
  ListSubscriptionsQuerySchema,
  ListSubscriptionsResponseSchema,
  MigrateSubscriptionBodySchema,
  MigrateSubscriptionHistoryDataRequestSchema,
  MigrateSubscriptionHistoryResponseSchema,
  PauseSubscriptionBodySchema,
  SearchSubscriptionsQuerySchema,
  SubscriptionSchema,
  SubscriptionSuccessResponseSchema,
  UpdateSubscriptionBodySchema,
  VerifyMigratedHistoricalDataRequestSchema,
  VerifyMigratedSubscriptionResponseSchema,
} from './schemas'

const c = initContract()

const options = {
  pathPrefix: '/subscriptions',
  commonResponses: {
    400: ErrorResponseSchema,
    404: ErrorResponseSchema,
    409: ErrorResponseSchema,
    500: ErrorResponseSchema,
  },
}

export const subscriptionServiceContract = c.router({
  getSubscriptionsByUser: {
    method: 'GET',
    path: '',
    query: GetSubscriptionsByUserQuerySchema,
    responses: {
      200: SubscriptionSchema.array(),
    },
  },
  searchSubscriptions: {
    method: 'GET',
    path: '/search',
    query: SearchSubscriptionsQuerySchema,
    responses: {
      200: SubscriptionSchema.array(),
    },
  },
  listSubscriptions: {
    method: 'GET',
    path: '/list',
    query: ListSubscriptionsQuerySchema,
    responses: {
      200: ListSubscriptionsResponseSchema,
    },
  },
  createSubscription: {
    method: 'POST',
    path: '',
    body: CreateSubscriptionBodySchema,
    responses: {
      201: SubscriptionSchema,
    },
  },
  getSubscription: {
    method: 'GET',
    path: '/:id',
    pathParams: SubscriptionSchema.pick({ id: true }),
    query: GetSubscriptionQuerySchema,
    responses: {
      200: SubscriptionSchema,
    },
  },
  updateSubscription: {
    method: 'PATCH',
    path: '/:id',
    pathParams: SubscriptionSchema.pick({ id: true }),
    body: UpdateSubscriptionBodySchema,
    responses: {
      200: SubscriptionSchema,
    },
  },
  pauseSubscription: {
    method: 'POST',
    path: '/:id/pause',
    pathParams: SubscriptionSchema.pick({ id: true }),
    body: PauseSubscriptionBodySchema,
    responses: {
      200: SubscriptionSchema,
    },
  },
  resumeSubscription: {
    method: 'POST',
    path: '/:id/resume',
    pathParams: SubscriptionSchema.pick({ id: true }),
    body: null,
    responses: {
      200: SubscriptionSchema,
    },
  },
  cancelSubscription: {
    method: 'POST',
    path: '/:id/cancel',
    pathParams: SubscriptionSchema.pick({ id: true }),
    body: CancelSubscriptionSchema,
    responses: {
      200: SubscriptionSchema,
    },
  },
  reactivateSubscription: {
    method: 'POST',
    path: '/reactivate/:userId',
    pathParams: SubscriptionSchema.pick({ userId: true }),
    body: null,
    responses: {
      200: SubscriptionSchema,
    },
  },
  removeSubscription: {
    method: 'DELETE',
    path: '/:id',
    pathParams: SubscriptionSchema.pick({ id: true }),
    body: null,
    responses: {
      200: SubscriptionSchema,
    },
  },
  pauseSubscriptionFeedingPlan: {
    method: 'POST',
    path: '/:id/feeding-plan/:feedingPlanId/pause',
    pathParams: SubscriptionSchema.pick({ id: true }).extend({
      feedingPlanId: z.string().uuid(),
    }),
    body: null,
    responses: {
      200: FeedingPlanSchema,
    },
  },
  activateSubscriptionFeedingPlan: {
    method: 'POST',
    path: '/:id/feeding-plan/:feedingPlanId/active',
    pathParams: SubscriptionSchema.pick({ id: true }).extend({
      feedingPlanId: z.string().uuid(),
    }),
    body: null,
    responses: {
      200: FeedingPlanSchema,
    },
  },
  cancelSubscriptionExtra: {
    method: 'POST',
    path: '/:id/extras/:extraId/cancel',
    pathParams: SubscriptionSchema.pick({ id: true }).extend({
      extraId: z.string().uuid(),
    }),
    body: CancelSubscriptionExtraBodySchema,
    responses: {
      200: ExtraSchema,
    },
  },
  createSubscriptionDiscount: {
    method: 'POST',
    description: 'Create a new subscription discount',
    path: '/:id/discounts',
    pathParams: SubscriptionSchema.pick({ id: true }),
    body: CreateSubscriptionDiscountBodySchema,
    responses: {
      201: CreateSubscriptionDiscountResponseSchema,
    },
  },
  deleteAllSubscriptionDiscounts: {
    method: 'DELETE',
    description: 'Soft-delete all subscription discounts',
    path: '/:id/discounts',
    pathParams: SubscriptionSchema.pick({ id: true }),
    query: DeleteMigratedSubscriptionDiscountQueryParamsSchema,
    body: DeleteSubscriptionDiscountBodySchema,
    responses: {
      204: z.undefined(),
    },
  },
  deleteSubscriptionDiscount: {
    method: 'DELETE',
    description: 'Soft-delete specific subscription discount',
    path: '/:subscriptionId/discounts/:subscriptionDiscountId',
    pathParams: DeleteSubscriptionPathParams,
    body: DeleteSubscriptionDiscountBodySchema,
    responses: {
      204: z.undefined(),
    },
  },
  migrateSubscriptions: {
    method: 'POST',
    description: 'This endpoint will only used by migration service to migrate data from laravel to phoenix',
    path: '/migration',
    body: z.object({
      dryRun: z.boolean().default(false),
      userId: z.string().uuid(),
      subscriptions: MigrateSubscriptionBodySchema.array(),
    }),
    responses: {
      200: SubscriptionSchema.array(),
    },
  },
  deleteMigratedSubscription: {
    method: 'DELETE',
    description: 'This endpoint will only used by migration service to delete the migrated data from laravel to phoenix',
    path: '/migration/:userId',
    pathParams: SubscriptionSchema.pick({ userId: true }),
    responses: {
      200: SubscriptionSuccessResponseSchema,
    },
  },
  verifyMigratedSubscriptions: {
    method: 'POST',
    description: 'Thie endpoint will be used to verify the migrated subscription',
    path: '/migrations/verify',
    body: z.object({
      dryRun: z.boolean().default(false),
      userId: z.string().uuid(),
      subscriptions: MigrateSubscriptionBodySchema.array(),
    }),
    responses: {
      200: VerifyMigratedSubscriptionResponseSchema,
    },
  },
  migrateHistoricalData: {
    method: 'POST',
    description: 'This endpoint will only used by migration service to migrate the historical data from laravel to phoenix for the existing subscriptions in phoenix',
    path: '/migration/history',
    body: MigrateSubscriptionHistoryDataRequestSchema,
    responses: {
      200: MigrateSubscriptionHistoryResponseSchema,
    },
  },
  verifyMigratedHistoricalData: {
    method: 'POST',
    description: 'This endpoint will be used by the migration to verify the migrated historical data for the subscription',
    path: '/migration/history/verify',
    body: VerifyMigratedHistoricalDataRequestSchema,
    responses: {
      200: VerifyMigratedSubscriptionResponseSchema,
    },
  },
  revertMigratedHistoricalData: {
    method: 'DELETE',
    description: 'This endpoint will only used by migration service to revert the migrated historical data from laravel to phoenix in case there is any issue after migration',
    path: '/migration/history/revert/:userId',
    pathParams: SubscriptionSchema.pick({ userId: true }),
    body: z.object({
      selectedHistoryTables: z.nativeEnum(HistoryTables).array().optional().describe('Subset of history tables to migrate. Defaults to all implemented tables.'),
    }).optional(),
    responses: {
      200: SubscriptionSuccessResponseSchema,
    },
  },
}, options)
